import type { HTMLAttributes } from 'react';
import { cn } from './cn.js';

export type BadgeTone = 'slate' | 'blue' | 'amber' | 'violet' | 'teal' | 'green' | 'red';

const tones: Record<BadgeTone, string> = {
  slate: 'bg-slate-100 text-slate-700 ring-slate-500/20 dark:bg-slate-800 dark:text-slate-300',
  blue: 'bg-blue-50 text-blue-700 ring-blue-600/20 dark:bg-blue-900/40 dark:text-blue-300',
  amber: 'bg-amber-50 text-amber-800 ring-amber-600/30 dark:bg-amber-900/40 dark:text-amber-300',
  violet:
    'bg-violet-50 text-violet-700 ring-violet-600/20 dark:bg-violet-900/40 dark:text-violet-300',
  teal: 'bg-teal-50 text-teal-700 ring-teal-600/20 dark:bg-teal-900/40 dark:text-teal-300',
  green:
    'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-900/40 dark:text-emerald-300',
  red: 'bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-900/40 dark:text-red-300',
};

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
}

export function Badge({ tone = 'slate', className, ...props }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset',
        tones[tone],
        className,
      )}
      {...props}
    />
  );
}

/**
 * Domēna statusu → krāsu toņa karte (pasūtījumi, reklamācijas, uzdevumi, dokumenti).
 * slate = melnraksts/pieprasīts · blue = piedāvājums/izvietots · amber = gaida/ražošanā ·
 * violet = ceļā · teal = piegādāts/inspicēts · green = norēķināts · red = atcelts/problēma.
 */
const STATUS_TONE: Record<string, BadgeTone> = {
  // Pasūtījuma dzīves cikls
  quote_requested: 'slate',
  offer_drafted: 'blue',
  offer_sent: 'blue',
  offer_approved: 'blue',
  placed: 'blue',
  accepted: 'amber',
  awaiting_confirmation: 'amber',
  confirmed: 'amber',
  production: 'amber',
  labelled: 'violet',
  loaded: 'violet',
  dispatched: 'violet',
  delivered: 'teal',
  inspected: 'teal',
  invoiced: 'green',
  paid: 'green',
  cancelled: 'red',
  // Reklamācijas / uzdevumi / vispārīgie
  open: 'amber',
  in_review: 'blue',
  resolved: 'green',
  rejected: 'red',
  closed: 'slate',
  done: 'green',
  overdue: 'red',
  pending: 'amber',
  active: 'green',
  expired: 'red',
  // CoA / dokumenti
  pass: 'green',
  fail: 'red',
};

export function statusTone(status: string): BadgeTone {
  return STATUS_TONE[status.toLowerCase()] ?? 'slate';
}

export interface StatusBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  status: string;
  /** Tulkotais teksts; ja nav dots, rāda pašu statusa kodu. */
  label?: string;
}

/** Krāsu-kodēts statusa marķieris — lietojams VISUR, kur agrāk drukāja jēlu enum. */
export function StatusBadge({ status, label, className, ...props }: StatusBadgeProps) {
  return (
    <Badge tone={statusTone(status)} className={className} {...props}>
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" aria-hidden />
      {label ?? status}
    </Badge>
  );
}
